import type { PurchaseOrder } from "../../redistribution/models/PurchaseOrder.ts";
import { getDivisionLabel } from "../../utils/divisionLabels.ts";

// "Distribution Center" is the buyer-facing name for what the data calls a
// division — the same grouping the vendor search modal shows above its
// warehouse list. Kept as `division` in code to match every other layer.
const DISTRIBUTION_CENTER_DIVISIONS = ["FL", "SE", "TX", "PR"];

export interface InProcessWindow {
  id: string;
  /** Toggle label. */
  label: string;
  days: number;
}

export const IN_PROCESS_WINDOWS: InProcessWindow[] = [
  { id: "six-weeks", label: "6 weeks", days: 42 },
  { id: "ninety-days", label: "90 days", days: 90 },
];

export interface DistributionCenterActivityRow {
  division: string;
  label: string;
  purchaseOrderCount: number;
}

interface ArrivalWindow {
  arrivalDateFrom: Date;
  arrivalDateTo: Date;
}

/**
 * The search API filters on arrival date, not ship date, so the window this
 * card asks for is "arriving within N days" rather than "shipping within N
 * days". The card labels itself accordingly.
 */
export function buildArrivalWindow(days: number, now: Date): ArrivalWindow {
  const arrivalDateFrom = new Date(now);
  arrivalDateFrom.setHours(0, 0, 0, 0);

  const arrivalDateTo = new Date(arrivalDateFrom);
  arrivalDateTo.setDate(arrivalDateTo.getDate() + days);
  arrivalDateTo.setHours(23, 59, 59, 999);

  return { arrivalDateFrom, arrivalDateTo };
}

/**
 * Counts distinct purchase orders per distribution center. Every known division
 * is returned even at zero, so a quiet DC reads as "nothing in process" rather
 * than silently vanishing from the card. Divisions present in the data but not
 * in the known list are appended rather than dropped.
 */
export function groupPurchaseOrdersByDistributionCenter(
  purchaseOrders: PurchaseOrder[]
): DistributionCenterActivityRow[] {
  const purchaseOrderNumbersByDivision = new Map<string, Set<string>>();

  for (const purchaseOrder of purchaseOrders) {
    const division = purchaseOrder.division.trim();
    if (!division) {
      continue;
    }

    const existing = purchaseOrderNumbersByDivision.get(division) ?? new Set();
    existing.add(purchaseOrder.purchaseOrderNumber);
    purchaseOrderNumbersByDivision.set(division, existing);
  }

  const unknownDivisions = [...purchaseOrderNumbersByDivision.keys()]
    .filter((division) => !DISTRIBUTION_CENTER_DIVISIONS.includes(division))
    .toSorted((a, b) => a.localeCompare(b));

  return [...DISTRIBUTION_CENTER_DIVISIONS, ...unknownDivisions].map(
    (division) => ({
      division,
      label: getDivisionLabel(division),
      purchaseOrderCount:
        purchaseOrderNumbersByDivision.get(division)?.size ?? 0,
    })
  );
}

export function totalPurchaseOrderCount(
  rows: DistributionCenterActivityRow[]
): number {
  return rows.reduce((total, row) => total + row.purchaseOrderCount, 0);
}
